import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Upload, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CITIES = ["Lucknow", "Gurugram", "Noida", "Delhi", "Mumbai", "Bangalore", "Pune", "Hyderabad", "Dubai", "Singapore"];
const TYPES = ["apartment", "villa", "plot", "commercial"];
const FURNISHING = ["unfurnished", "semi", "fully"];

export default function PostProperty() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "", city: "Lucknow", locality: "", type: "apartment", listing_type: "sale",
    bhk: 2, bathrooms: 2, area_sqft: 1200, price: 5000000, description: "",
    amenities: [], furnishing: "unfurnished", contact_phone: "", images: [],
  });
  const [amenityInput, setAmenityInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!loading && !user) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="post-property-auth-block">
        <div className="overline mb-3">Authentication required</div>
        <h1 className="font-serif text-4xl text-slate-900 mb-4">Sign in to list your property</h1>
        <p className="text-slate-600 mb-8">Create a free account to post listings and reach 12,000+ verified buyers.</p>
        <div className="flex justify-center gap-3">
          <Link to="/login" className="px-6 py-3 border border-slate-200 text-slate-800 hover:border-[#D4A437]" data-testid="post-go-login">Sign in</Link>
          <Link to="/register" className="px-6 py-3 bg-[#D4A437] text-white hover:bg-[#D4A437]" data-testid="post-go-register">Create Account</Link>
        </div>
      </div>
    );
  }

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((s) => ({ ...s, images: [...s.images, data.url] }));
      }
      toast.success(`${files.length} image(s) uploaded`);
    } catch (err) {
      toast.error("Upload failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx) => setForm((s) => ({ ...s, images: s.images.filter((_, i) => i !== idx) }));

  const addAmenity = () => {
    if (!amenityInput.trim()) return;
    setForm((s) => ({ ...s, amenities: [...s.amenities, amenityInput.trim()] }));
    setAmenityInput("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        bhk: Number(form.bhk),
        bathrooms: Number(form.bathrooms),
        area_sqft: Number(form.area_sqft),
        price: Number(form.price),
      };
      const { data } = await api.post("/properties", payload);
      toast.success("Property posted successfully");
      navigate("/dashboard");
    } catch (err) {
      toast.error("Could not post: " + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const fld = "w-full bg-slate-50 border border-slate-100 px-4 py-3 focus:outline-none focus:border-amber-500 text-sm";

  return (
    <div className="min-h-screen pb-32" data-testid="post-property-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12 pt-12">
        <div className="overline mb-3">Sellers</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-light text-slate-900">Post your property</h1>
        <p className="text-slate-600 mt-3 max-w-2xl font-light">List your residence on EstateHaven. We'll verify and amplify it across our network.</p>
      </div>

      <form onSubmit={handleSubmit} data-testid="post-property-form" className="max-w-4xl mx-auto px-6 md:px-12 mt-16 space-y-12">
        <div>
          <h3 className="font-serif text-2xl text-slate-900 mb-6">Basic Information</h3>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <label className="overline block mb-2">Listing Title</label>
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={fld} data-testid="pp-title" placeholder="3BHK Sea-facing Apartment, Worli" />
            </div>
            <div>
              <label className="overline block mb-2">City</label>
              <select value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={fld} data-testid="pp-city">
                {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Locality</label>
              <input required value={form.locality} onChange={(e) => setForm({ ...form, locality: e.target.value })} className={fld} data-testid="pp-locality" />
            </div>
            <div>
              <label className="overline block mb-2">Type</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={fld} data-testid="pp-type">
                {TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-2">For</label>
              <select value={form.listing_type} onChange={(e) => setForm({ ...form, listing_type: e.target.value })} className={fld} data-testid="pp-listing-type">
                <option value="sale">Sale</option>
                <option value="rent">Rent</option>
              </select>
            </div>
          </div>
        </div>

        <div>
          <h3 className="font-serif text-2xl text-slate-900 mb-6">Specifications</h3>
          <div className="grid md:grid-cols-4 gap-5">
            <div>
              <label className="overline block mb-2">BHK</label>
              <input type="number" min={1} value={form.bhk} onChange={(e) => setForm({ ...form, bhk: e.target.value })} className={fld} data-testid="pp-bhk" />
            </div>
            <div>
              <label className="overline block mb-2">Bathrooms</label>
              <input type="number" min={1} value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} className={fld} data-testid="pp-baths" />
            </div>
            <div>
              <label className="overline block mb-2">Area (sqft)</label>
              <input type="number" required value={form.area_sqft} onChange={(e) => setForm({ ...form, area_sqft: e.target.value })} className={fld} data-testid="pp-area" />
            </div>
            <div>
              <label className="overline block mb-2">Price (₹)</label>
              <input type="number" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={fld} data-testid="pp-price" />
            </div>
            <div className="md:col-span-2">
              <label className="overline block mb-2">Furnishing</label>
              <select value={form.furnishing} onChange={(e) => setForm({ ...form, furnishing: e.target.value })} className={fld} data-testid="pp-furnishing">
                {FURNISHING.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="overline block mb-2">Contact Phone</label>
              <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className={fld} data-testid="pp-contact-phone" />
            </div>
          </div>
        </div>

        <div>
          <h3 className="font-serif text-2xl text-slate-900 mb-6">Description</h3>
          <textarea rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={fld} data-testid="pp-description"
            placeholder="Tell buyers what makes this property special..." />
        </div>

        <div>
          <h3 className="font-serif text-2xl text-slate-900 mb-6">Amenities</h3>
          <div className="flex gap-2 mb-3">
            <input value={amenityInput} onChange={(e) => setAmenityInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addAmenity())}
              className={fld} placeholder="e.g. Swimming Pool" data-testid="pp-amenity-input" />
            <button type="button" onClick={addAmenity} className="px-5 bg-slate-100 text-slate-800 hover:bg-slate-700 text-sm" data-testid="pp-amenity-add">Add</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {form.amenities.map((a, i) => (
              <span key={i} className="px-3 py-1.5 border border-slate-200 text-xs flex items-center gap-2">
                {a}
                <button type="button" onClick={() => setForm((s) => ({ ...s, amenities: s.amenities.filter((_, x) => x !== i) }))}>
                  <X className="w-3 h-3 text-slate-500 hover:text-[#D4A437]" />
                </button>
              </span>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-serif text-2xl text-slate-900 mb-6">Photos</h3>
          <label className="block border border-dashed border-slate-200 hover:border-[#D4A437] cursor-pointer p-12 text-center transition-colors">
            <input type="file" multiple accept="image/*" className="hidden" onChange={handleUpload} data-testid="pp-image-input" />
            <ImagePlus className="w-8 h-8 mx-auto mb-3 text-[#D4A437]" strokeWidth={1.5} />
            <div className="font-serif text-xl text-slate-900">{uploading ? "Uploading..." : "Drop images or click to browse"}</div>
            <div className="text-xs text-slate-500 mt-2">JPG, PNG, WebP up to 10MB each</div>
          </label>
          {form.images.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-6">
              {form.images.map((img, i) => (
                <div key={i} className="relative aspect-square bg-slate-50 border border-slate-200 overflow-hidden">
                  <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeImage(i)} data-testid={`pp-remove-img-${i}`}
                    className="absolute top-1 right-1 w-6 h-6 bg-white/80 text-slate-800 hover:bg-[#D4A437] hover:text-white flex items-center justify-center">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 pt-8 flex justify-end gap-4">
          <button type="button" onClick={() => navigate(-1)} className="px-6 py-3 border border-slate-200 text-slate-800 hover:border-[#D4A437]">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="pp-submit"
            className="px-8 py-3 bg-[#D4A437] text-white hover:bg-[#D4A437] disabled:opacity-60 font-medium">
            {submitting ? "Posting..." : "Publish Listing →"}
          </button>
        </div>
      </form>
    </div>
  );
}
